/**
 * Encapsulates the full J-code add flow used by the CPT codes page:
 * issuing the create request, refreshing auth-on-file data on success,
 * surfacing toasts, syncing the in-progress flag to the modal's Redux
 * metadata, and exposing handlers ready for the
 * `AddNewCptCodeModal`'s prop API.
 *
 * @module useJCodeAddFlow
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useDispatch, useSelector } from "react-redux";
import { AddNewCptCodeFormData } from "../../../../../components/modals/addNewCptCodeModal/addNewCptCodeModal";
import { ToastMessages } from "../../../../../components/toasts/toastMessages";
import { useDataAccumulatorForSingleOrder } from "../../../../../context/dataAccumulatorForSingleOrder";
import { GeneralOrderDetails } from "../../../../../data-model/medicalPaOrdersModel";
import { closeModal, setMetaData } from "../../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";
import { useAddJCode } from "./useAddJCode";

/** Handlers returned by {@link useJCodeAddFlow}. */
interface JCodeAddFlowHandlers {
  /**
   * Issues the create request with the form payload and flips the
   * modal's Redux `saving` flag.
   */
  handleAdd: (formData: AddNewCptCodeFormData) => void;
  /** Aborts any in-flight create and closes the modal. */
  handleClose: () => void;
}

/**
 * React hook that wires the J-code add flow to the
 * {@link AddNewCptCodeModal}. Internally uses `useAddJCode` for
 * abort-aware fetching and dispatches `closeModal` / `setMetaData` so
 * the modal's loading and lifecycle stay in sync.
 *
 * @param generalOrderDetails - Order context used to compose the
 *   create payload (order_id and org_id).
 * @returns Memo-stable handlers for the modal's `onSave` and `onClose`
 *   props.
 */
export function useJCodeAddFlow(
  generalOrderDetails: GeneralOrderDetails | null | undefined,
): JCodeAddFlowHandlers {
  const dispatch = useDispatch<AppDispatch>();
  const metaData = useSelector((state: RootState) => state.modal.metaData);
  const { fetchAuthOnFileDetails } = useDataAccumulatorForSingleOrder();

  const { triggerAdd, abort } = useAddJCode({
    onSuccess: async () => {
      await fetchAuthOnFileDetails(
        generalOrderDetails?.order_id ?? "",
        getOrgIdForFetchExternalWorklist() ?? "",
      );
      dispatch(closeModal());
      toast.success(ToastMessages.CPT_CODE_ADDED_SUCCESSFULLY);
    },
    onFailure: () => {
      toast.error(ToastMessages.CPT_CODE_ADD_FAILED);
    },
  });

  const handleAdd = (formData: AddNewCptCodeFormData) => {
    triggerAdd({
      order_id: generalOrderDetails?.order_id ?? "",
      org_id: getOrgIdForFetchExternalWorklist() ?? "",
      j_code: {
        j_code: formData.jCode.trim(),
        medicine_name: formData.jCodeDescription.trim(),
        auth_status: formData.authStatusId,
      },
    });
    dispatch(setMetaData({ ...metaData, saving: true }));
  };

  const handleClose = () => {
    dispatch(closeModal());
    abort();
  };

  return { handleAdd, handleClose };
}
